/**
 * TanStack Query hookovi nad tipiziranim klijentom (@sm/api). Ključevi su hijerarhijski
 * da bi SSE događaji mogli invalidirati čitave grupe (`['projects']`, `['production']`...).
 */
import type {
  AccountDeletionRequest,
  ClearanceRuleInput,
  ClearanceRulePatch,
  CreateProjectRequest,
  DecisionRequest,
  DeviceHealth,
  DevicePlatform,
  DocumentType,
  EmailChangeRequest,
  FeatureKey,
  FeatureState,
  InviteUserRequest,
  Me,
  Organization,
  Phase,
  PhotoCategory,
  PhotoTagInput,
  PhotoTagPatch,
  PositionInput,
  PositionPatch,
  ProductionListKind,
  ProjectSort,
  Role,
  Station,
  StationStatus,
  UpdateMeRequest,
  UpdateOrganizationRequest,
  UpdatePhotoRequest,
  UpdateProjectRequest,
  UpdateUserRequest,
} from '@sm/api';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api, postMultipart, unwrap } from './api';

export interface ProjectListParams {
  q?: string;
  phase?: Phase;
  late?: boolean;
  withWarnings?: boolean;
  active?: boolean;
  surveyorId?: string;
  page?: number;
  pageSize?: number;
  sort?: ProjectSort;
}

export const qk = {
  me: ['me'] as const,
  dashboard: ['dashboard'] as const,
  analytics: ['analytics'] as const,
  projects: (p?: ProjectListParams) => ['projects', 'list', p ?? {}] as const,
  project: (id: string) => ['projects', 'detail', id] as const,
  projectHistory: (id: string) => ['projects', 'detail', id, 'history'] as const,
  positions: (id: string) => ['projects', 'detail', id, 'positions'] as const,
  projectPhotos: (id: string) => ['projects', 'detail', id, 'photos'] as const,
  projectDocuments: (id: string) => ['projects', 'detail', id, 'documents'] as const,
  measurements: (status?: string) => ['measurements', 'list', status ?? 'all'] as const,
  measurement: (id: string) => ['measurements', 'detail', id] as const,
  photoTags: (id: string) => ['photos', id, 'tags'] as const,
  photos: (p: PhotoListParams) => ['photos', 'list', p] as const,
  orders: ['production', 'orders'] as const,
  stationLoad: ['production', 'load'] as const,
  productionList: (id: string, kind: ProductionListKind) =>
    ['production', 'orders', id, 'lists', kind] as const,
  notifications: ['notifications'] as const,
  users: (role?: Role) => ['users', role ?? 'all'] as const,
  organization: ['settings', 'organization'] as const,
  features: ['settings', 'features'] as const,
  rules: ['settings', 'clearance-rules'] as const,
  sessions: ['auth', 'sessions'] as const,
  identities: ['auth', 'identities'] as const,
  config: ['config'] as const,
  onboarding: ['onboarding'] as const,
  invite: (token: string) => ['auth', 'invite', token] as const,
  devices: (p?: DeviceListParams) => ['devices', 'list', p ?? {}] as const,
  supportReports: (p?: SupportReportListParams) => ['support', 'list', p ?? {}] as const,
  supportReport: (id: string) => ['support', 'detail', id] as const,
  subscription: ['settings', 'subscription'] as const,
  publicPlans: ['public', 'plans'] as const,
  supportAccess: ['settings', 'support-access'] as const,
};

// ---------------------------------------------------------------- me
export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: () => unwrap(api.GET('/me')),
  staleTime: 60_000,
  retry: false,
});

export function useMe() {
  return useQuery(meQuery);
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeRequest) => unwrap(api.PATCH('/me', { body })),
    onSuccess: (me: Me) => qc.setQueryData(qk.me, me),
  });
}

/** Promjena e-pošte: link ide na novu adresu, `Me.pendingEmail` čeka potvrdu (202). */
export function useRequestEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmailChangeRequest) => unwrap(api.POST('/me/email-change', { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.me }),
  });
}

/** Zahtjev za brisanje naloga (202 → sesija je opozvana, klijent ide na prijavu). */
export function useRequestAccountDeletion() {
  return useMutation({
    mutationFn: (body: AccountDeletionRequest) =>
      unwrap(api.POST('/me/deletion-request', { body })),
  });
}

// ---------------------------------------------------------------- dashboard / analytics
export function useDashboard(enabled = true) {
  return useQuery({
    queryKey: qk.dashboard,
    queryFn: () => unwrap(api.GET('/dashboard')),
    enabled,
  });
}

export function useAnalytics() {
  return useQuery({ queryKey: qk.analytics, queryFn: () => unwrap(api.GET('/analytics')) });
}

// ---------------------------------------------------------------- projects
export function useProjects(params: ProjectListParams, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.projects(params),
    queryFn: () => unwrap(api.GET('/projects', { params: { query: params } })),
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
  });
}

export function useProject(id: string, enabled = true) {
  return useQuery({
    queryKey: qk.project(id),
    queryFn: () => unwrap(api.GET('/projects/{id}', { params: { path: { id } } })),
    enabled,
  });
}

export function useProjectHistory(id: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: qk.projectHistory(id),
    queryFn: () =>
      unwrap(
        api.GET('/projects/{id}/history', {
          params: { path: { id }, query: { page: 1, pageSize: 100 } },
        }),
      ),
  });
}

export function usePositions(id: string, enabled = true) {
  return useQuery({
    queryKey: qk.positions(id),
    queryFn: () => unwrap(api.GET('/projects/{id}/positions', { params: { path: { id } } })),
    enabled,
  });
}

export function useProjectPhotos(id: string, enabled = true) {
  return useQuery({
    queryKey: qk.projectPhotos(id),
    queryFn: () => unwrap(api.GET('/projects/{id}/photos', { params: { path: { id } } })),
    enabled,
  });
}

export interface PhotoListParams {
  category?: PhotoCategory;
  projectId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

/** Arhiva fotografija tenanta (`GET /photos`). */
export function usePhotos(params: PhotoListParams, enabled = true) {
  return useQuery({
    queryKey: qk.photos(params),
    queryFn: () => unwrap(api.GET('/photos', { params: { query: params } })),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useProjectDocuments(id: string, enabled = true) {
  return useQuery({
    queryKey: qk.projectDocuments(id),
    queryFn: () => unwrap(api.GET('/projects/{id}/documents', { params: { path: { id } } })),
    enabled,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectRequest) => unwrap(api.POST('/projects', { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}

export function useGenerateDocument(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: DocumentType) =>
      unwrap(
        api.POST('/projects/{id}/documents/{type}/generate', {
          params: { path: { id: projectId, type } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.projectDocuments(projectId) });
      void qc.invalidateQueries({ queryKey: qk.projectHistory(projectId) });
    },
  });
}

export function useCreateProductionOrder(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(api.POST('/projects/{id}/production-order', { params: { path: { id: projectId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['production'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}

/** Ručni prelaz faze (isporuka → montaža → završeno), `projects.write`. */
export function useAdvancePhase(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { phase: Phase; note?: string }) =>
      unwrap(api.POST('/projects/{id}/phase', { params: { path: { id: projectId } }, body })),
    onSuccess: (p) => {
      qc.setQueryData(qk.project(projectId), p);
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}

/** Izmjena projekta (`projects.write`); svako polje ide u istoriju (staro → novo). */
export function useUpdateProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProjectRequest) =>
      unwrap(api.PATCH('/projects/{id}', { params: { path: { id: projectId } }, body })),
    onSuccess: (p) => {
      qc.setQueryData(qk.project(projectId), p);
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}

/** Soft delete projekta (administrator), potvrda šifrom; projekat u proizvodnji vraća 409. */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm: string }) =>
      unwrap(api.DELETE('/projects/{id}', { params: { path: { id }, query: { confirm } } })),
    onSuccess: () => {
      // detalj obrisanog projekta se ne osvježava (404); stranica prelazi na listu
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
      void qc.invalidateQueries({ queryKey: qk.onboarding });
    },
  });
}

function invalidateProject(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  void qc.invalidateQueries({ queryKey: qk.project(projectId) });
  void qc.invalidateQueries({ queryKey: ['measurements'] });
  void qc.invalidateQueries({ queryKey: qk.dashboard });
}

export function useCreatePosition(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PositionInput) =>
      unwrap(api.POST('/projects/{id}/positions', { params: { path: { id: projectId } }, body })),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

/** Izmjena pozicije; za zaključanu poziciju (ispravka) tijelo nosi `correctionReason`. */
export function useUpdatePosition(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PositionPatch }) =>
      unwrap(api.PATCH('/positions/{id}', { params: { path: { id } }, body })),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useDeletePosition(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/positions/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

export function useAcknowledgeWarning(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { positionId: string; warningId: string; note: string }) =>
      unwrap(
        api.POST('/positions/{id}/warnings/{warningId}/acknowledge', {
          params: { path: { id: v.positionId, warningId: v.warningId } },
          body: { note: v.note },
        }),
      ),
    onSuccess: () => invalidateProject(qc, projectId),
  });
}

// ---------------------------------------------------------------- onboarding
export function useOnboarding(enabled = true) {
  return useQuery({
    queryKey: qk.onboarding,
    queryFn: () => unwrap(api.GET('/onboarding')),
    enabled,
  });
}

export function useDismissOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/onboarding/dismiss')),
    onSuccess: () =>
      qc.setQueryData(qk.onboarding, (o: { dismissed: boolean } | undefined) =>
        o ? { ...o, dismissed: true } : o,
      ),
  });
}

// ---------------------------------------------------------------- measurements
export function useMeasurements(status?: 'submitted') {
  return useQuery({
    queryKey: qk.measurements(status),
    queryFn: () =>
      unwrap(
        api.GET('/measurements', {
          params: { query: { ...(status ? { status } : {}), page: 1, pageSize: 100 } },
        }),
      ),
  });
}

export function useMeasurement(id: string | undefined) {
  return useQuery({
    queryKey: qk.measurement(id ?? ''),
    queryFn: () => unwrap(api.GET('/measurements/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: !!id,
  });
}

export function useDecideMeasurement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecisionRequest }) =>
      unwrap(api.POST('/measurements/{id}/decision', { params: { path: { id } }, body })),
    onSuccess: (detail) => {
      qc.setQueryData(qk.measurement(detail.id), detail);
      void qc.invalidateQueries({ queryKey: ['measurements', 'list'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}

// ---------------------------------------------------------------- photos
export function usePhotoTags(photoId: string | undefined) {
  return useQuery({
    queryKey: qk.photoTags(photoId ?? ''),
    queryFn: () =>
      unwrap(api.GET('/photos/{id}/tags', { params: { path: { id: photoId ?? '' } } })),
    enabled: !!photoId,
  });
}

function invalidatePhotos(qc: ReturnType<typeof useQueryClient>, projectId?: string) {
  void qc.invalidateQueries({ queryKey: ['photos'] });
  if (projectId) void qc.invalidateQueries({ queryKey: qk.project(projectId) });
  else void qc.invalidateQueries({ queryKey: ['projects'] });
}

export function useUpdatePhoto(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePhotoRequest }) =>
      unwrap(api.PATCH('/photos/{id}', { params: { path: { id } }, body })),
    onSuccess: () => invalidatePhotos(qc, projectId),
  });
}

export function useDeletePhoto(projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/photos/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidatePhotos(qc, projectId),
  });
}

export function useCreatePhotoTag(photoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PhotoTagInput) =>
      unwrap(api.POST('/photos/{id}/tags', { params: { path: { id: photoId } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.photoTags(photoId) });
      void qc.invalidateQueries({ queryKey: ['photos', 'list'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdatePhotoTag(photoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PhotoTagPatch }) =>
      unwrap(api.PATCH('/photo-tags/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.photoTags(photoId) }),
  });
}

export function useDeletePhotoTag(photoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/photo-tags/{id}', { params: { path: { id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.photoTags(photoId) });
      void qc.invalidateQueries({ queryKey: ['photos', 'list'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ---------------------------------------------------------------- production
export function useProductionOrders(enabled = true) {
  return useQuery({
    enabled,
    queryKey: qk.orders,
    queryFn: () => unwrap(api.GET('/production/orders', { params: { query: {} } })),
  });
}

export function useStationLoad() {
  return useQuery({
    queryKey: qk.stationLoad,
    queryFn: () => unwrap(api.GET('/production/stations/load')),
  });
}

export function useProductionList(orderId: string | undefined, kind: ProductionListKind | null) {
  return useQuery({
    queryKey: qk.productionList(orderId ?? '', kind ?? 'cutting'),
    queryFn: () =>
      unwrap(
        api.GET('/production/orders/{id}/lists/{kind}', {
          params: { path: { id: orderId ?? '', kind: kind ?? 'cutting' } },
        }),
      ),
    enabled: !!orderId && !!kind,
  });
}

export function useUpdateStationStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { orderId: string; station: Station; status: StationStatus }) =>
      unwrap(
        api.PATCH('/production/orders/{id}/stations/{station}', {
          params: { path: { id: v.orderId, station: v.station } },
          body: { status: v.status },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['production'] }),
  });
}

// ---------------------------------------------------------------- notifications
export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: qk.notifications,
    queryFn: () => unwrap(api.GET('/notifications', { params: { query: { limit: 50 } } })),
    enabled,
    refetchInterval: 120_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/notifications/{id}/read', { params: { path: { id } } })),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.notifications }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/notifications/read-all')),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.notifications }),
  });
}

// ---------------------------------------------------------------- users
export function useUsers(role?: Role, enabled = true) {
  return useQuery({
    queryKey: qk.users(role),
    queryFn: () => unwrap(api.GET('/users', { params: { query: role ? { role } : {} } })),
    enabled,
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteUserRequest) => unwrap(api.POST('/users/invite', { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserRequest }) =>
      unwrap(api.PATCH('/users/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

/** Administrator odjavljuje korisnika sa svih uređaja (`DELETE /users/{id}/sessions`). */
export function useRevokeUserSessions() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/users/{id}/sessions', { params: { path: { id } } })),
  });
}

/** Administrator poništava zakazano brisanje naloga korisnika (nalog se ponovo aktivira). */
export function useCancelUserDeletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/users/{id}/deletion-request', { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/users/{id}/invite', { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ---------------------------------------------------------------- settings
export function useOrganization(enabled = true) {
  return useQuery({
    queryKey: qk.organization,
    queryFn: () => unwrap(api.GET('/settings/organization')),
    enabled,
  });
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOrganizationRequest) =>
      unwrap(api.PATCH('/settings/organization', { body })),
    onSuccess: (org: Organization) => {
      qc.setQueryData(qk.organization, org);
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return postMultipart<Organization>('/settings/organization/logo', form);
    },
    onSuccess: (org) => {
      qc.setQueryData(qk.organization, org);
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useDeleteLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/settings/organization/logo')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.organization });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useFeatures(enabled = true) {
  return useQuery({
    queryKey: qk.features,
    queryFn: () => unwrap(api.GET('/features')),
    enabled,
  });
}

export function useSetFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, state }: { key: FeatureKey; state: FeatureState }) =>
      unwrap(api.PUT('/features/{key}', { params: { path: { key } }, body: { state } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.features });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useClearanceRules() {
  return useQuery({
    queryKey: qk.rules,
    queryFn: () => unwrap(api.GET('/clearance-rules')),
    staleTime: 5 * 60_000,
  });
}

export function useSaveClearanceRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; body: ClearanceRuleInput | ClearanceRulePatch }) =>
      v.id
        ? unwrap(
            api.PATCH('/clearance-rules/{id}', {
              params: { path: { id: v.id } },
              body: v.body as ClearanceRulePatch,
            }),
          )
        : unwrap(api.POST('/clearance-rules', { body: v.body as ClearanceRuleInput })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.rules }),
  });
}

export function useDeleteClearanceRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/clearance-rules/{id}', { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.rules }),
  });
}

// ---------------------------------------------------------------- sessions / password
export function useSessions() {
  return useQuery({ queryKey: qk.sessions, queryFn: () => unwrap(api.GET('/auth/sessions')) });
}

/** Povezani nalozi (Google/Apple) prijavljenog korisnika. */
export function useIdentities() {
  return useQuery({ queryKey: qk.identities, queryFn: () => unwrap(api.GET('/me/identities')) });
}

export function useUnlinkIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/me/identities/{id}', { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.identities }),
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/auth/sessions/{id}', { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sessions }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      unwrap(api.POST('/auth/change-password', { body })),
  });
}

// ---------------------------------------------------------------- uređaji / podrška
export interface DeviceListParams {
  health?: DeviceHealth;
  platform?: DevicePlatform;
  userId?: string;
  q?: string;
}

/** Uređaji firme sa stanjem sinhronizacije (administrator, direktor); SSE `device.updated` osvježava listu. */
export function useDevices(params: DeviceListParams = {}, enabled = true) {
  return useQuery({
    queryKey: qk.devices(params),
    queryFn: () => unwrap(api.GET('/devices', { params: { query: params } })),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    enabled,
  });
}

/** Administrator opoziva sesiju uređaja (izgubljen tablet): postojeći `DELETE /users/{id}/sessions/{sessionId}`. */
export function useRevokeDeviceSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, sessionId }: { userId: string; sessionId: string }) =>
      unwrap(
        api.DELETE('/users/{id}/sessions/{sessionId}', {
          params: { path: { id: userId, sessionId } },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['devices'] }),
  });
}

export interface SupportReportListParams {
  code?: string;
  deviceId?: string;
  userId?: string;
  page?: number;
  pageSize?: number;
}

export function useSupportReports(params: SupportReportListParams = {}) {
  return useQuery({
    queryKey: qk.supportReports(params),
    queryFn: () => unwrap(api.GET('/support/reports', { params: { query: params } })),
    placeholderData: keepPreviousData,
  });
}

export function useSupportReport(id: string | undefined) {
  return useQuery({
    queryKey: qk.supportReport(id ?? ''),
    queryFn: () => unwrap(api.GET('/support/reports/{id}', { params: { path: { id: id! } } })),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------- paket / pristup podrške
/** Paket firme (administrator, `settings.manage`): plan, status, limiti i iskorištenost. */
export const subscriptionQuery = queryOptions({
  queryKey: qk.subscription,
  queryFn: () => unwrap(api.GET('/subscription')),
  staleTime: 60_000,
});
export function useSubscription(enabled = true) {
  return useQuery({ ...subscriptionQuery, enabled });
}
/** Javni paketi (nazivi za izbor u zahtjevu za promjenu; cijene se u panelu ne prikazuju). */
export function usePublicPlans(enabled = true) {
  return useQuery({
    queryKey: qk.publicPlans,
    queryFn: () => unwrap(api.GET('/public/plans')),
    staleTime: 10 * 60_000,
    enabled,
  });
}
export function useRequestPlanChange() {
  return useMutation({
    mutationFn: (body: { planCode: string | null; message: string }) =>
      unwrap(api.POST('/subscription/change-request', { body })),
  });
}
/** Pristupi podrške NENASAL nalozima firme (impersonacija), najnovije prvo. */
export function useSupportAccess(enabled = true) {
  return useQuery({
    queryKey: qk.supportAccess,
    queryFn: () =>
      unwrap(api.GET('/settings/support-access', { params: { query: { page: 1, pageSize: 20 } } })),
    enabled,
  });
}
